/**
 * レビュー教師データページ
 *
 * Figma レビューコメント (ブランド別 BQ テーブル creative.review_examples_{brandId}) を
 * キーワード・ブランドで検索して閲覧する。読み取り専用 (更新は spreadsheet-sync
 * パイプラインの日次取り込み)。
 *
 * @graph-stack creative
 * @graph-domain Marketing
 * @graph-business Figmaレビューコメント由来の教師データをキーワード・ファイル名で検索閲覧する読み取り専用画面。
 * @graph-connects api [calls] reviewExamples.search による BQ 検索
 */
import { useQuery } from '@tanstack/react-query';
import { ExternalLink, Search } from 'lucide-react';
import { useState, type JSX } from 'react';
import { orpc } from '../api/orpc.js';
import { useBrandScope } from '../components/brand-scope-context.js';
import { EmptyState, GhostButton, INPUT_CLASS, SectionHeading } from '../components/ui.js';

/** 1 ページの取得件数。 @graph-connects none */
const PAGE_SIZE = 20;

/**
 * レビュー教師データページ。
 * @returns ページ要素
 * @graph-connects api [calls] reviewExamples.search による BQ 検索
 */
export function ReviewExamplesPage(): JSX.Element {
  const brandScope = useBrandScope();
  const [keywordInput, setKeywordInput] = useState('');
  const [keyword, setKeyword] = useState('');
  const [offset, setOffset] = useState(0);

  const searchQuery = useQuery(
    orpc.reviewExamples.search.queryOptions({
      input: {
        keyword: keyword === '' ? undefined : keyword,
        brand: brandScope.scope === '' ? undefined : brandScope.scope,
        limit: PAGE_SIZE,
        offset,
      },
    }),
  );
  const items = searchQuery.data?.items ?? [];
  const hasMore = searchQuery.data?.hasMore ?? false;

  return (
    <div>
      <SectionHeading title="Review Examples" />
      <p className="mb-4 text-body text-muted">
        デザイナーの過去レビューコメント (Figma コメントストック由来)。AI レビューの few-shot
        例として MCP からも参照される。日次で自動取り込み。ブランドごとにシートを分けて
        取り込み、サイドバーのブランド切替に追従する。
      </p>

      <div className="mb-5 flex gap-2">
        <input
          className={INPUT_CLASS}
          placeholder="コメント本文をキーワード検索…"
          value={keywordInput}
          onChange={(event) => {
            setKeywordInput(event.target.value);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              setKeyword(keywordInput.trim());
              setOffset(0);
            }
          }}
        />
        <button
          type="button"
          aria-label="検索"
          className="flex items-center gap-1 rounded-lg bg-accent px-4 text-sm font-bold text-on-accent hover:brightness-110"
          onClick={() => {
            setKeyword(keywordInput.trim());
            setOffset(0);
          }}
        >
          <Search size={14} />
          検索
        </button>
      </div>

      {searchQuery.isLoading ? (
        <EmptyState message="検索中…" />
      ) : items.length === 0 ? (
        <EmptyState message="該当するコメントがありません (初回取り込み前の可能性があります)" />
      ) : (
        <div className="space-y-3">
          {items.map((example) => (
            <div
              key={example.commentId}
              className="rounded-xl border border-glass-line bg-card p-4"
            >
              <p className="text-body leading-relaxed text-foreground">{example.message}</p>
              <div className="mt-2 flex flex-wrap items-center gap-3 text-caption text-muted">
                <span className="font-semibold text-accent-text">{example.author}</span>
                <span className="truncate">{example.fileName}</span>
                {example.commentedAt !== null && <span>{example.commentedAt.slice(0, 10)}</span>}
                {example.commentUrl !== '' && (
                  <a
                    href={example.commentUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-link hover:underline"
                  >
                    <ExternalLink size={11} />
                    Figma で開く
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-5 flex justify-between">
        {offset > 0 ? (
          <GhostButton
            onClick={() => {
              setOffset(Math.max(0, offset - PAGE_SIZE));
            }}
          >
            ← 前へ
          </GhostButton>
        ) : (
          <span />
        )}
        {hasMore && (
          <GhostButton
            onClick={() => {
              setOffset(offset + PAGE_SIZE);
            }}
          >
            次へ →
          </GhostButton>
        )}
      </div>
    </div>
  );
}
